import type { AgentMessage, MessageAttachment, TranscriptItem } from 'fe-be-utils';
import { INTERACTIVE_TOOL_NAMES } from 'fe-be-utils';
import { Download, FileText, Headphones, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useState } from 'react';
import { API_BASE_URL } from '../constants';
import { cn } from '../lib/utils';
import type { WidgetTheme } from '../types/theme';
import { formatMessageContent } from '../utils/message-formatter';
import { getNativeApp } from '../utils/native-app';
import { getBackgroundStyle } from '../utils/theme';
import { Button } from './ui/button';

const isNativeApp = getNativeApp() !== null;

const proxyAttachmentUrl = (url: string, chatId?: string): string => {
  if (!chatId || !API_BASE_URL) return url;
  return `${API_BASE_URL}/app/public/widget/chats/${chatId}/attachment-proxy?url=${encodeURIComponent(url)}`;
};

type ChatConversationProps = {
  messages: TranscriptItem[];
  removeUserOptions?: (content: string | Array<unknown> | null | undefined) => string;
  className?: string;
  agentName?: string;
  theme: WidgetTheme;
  chatId?: string;
  endedAt?: string;
  csatIsSatisfied?: boolean;
  onCsatRatingChange?: (isSatisfied: boolean) => void;
  componentDismissed?: boolean;
};

export const ChatConversation = ({
  messages,
  removeUserOptions = (content) => (typeof content === 'string' ? content : ''),
  className,
  agentName,
  theme,
  chatId,
  endedAt,
  csatIsSatisfied: initialCsatIsSatisfied,
  onCsatRatingChange,
  componentDismissed
}: ChatConversationProps) => {
  const [csatIsSatisfied, setCsatIsSatisfied] = useState<boolean | undefined>(initialCsatIsSatisfied);
  const [viewerImage, setViewerImage] = useState<string | null>(null);

  const handleRatingClick = (isSatisfied: boolean) => {
    setCsatIsSatisfied(isSatisfied);
    onCsatRatingChange?.(isSatisfied);
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }

    const isYesterday = date.toDateString() === new Date(now.getTime() - 86400000).toDateString();
    if (isYesterday) {
      return `Yesterday ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }

    return (
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' +
      date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  const shouldShowTimeSeparator = (current: TranscriptItem, previous?: TranscriptItem) => {
    if (!previous) return false;

    // If current message has no timestamp, use previous message's timestamp for comparison
    const currentTimestamp = current.createdAt || previous.createdAt;
    if (!currentTimestamp) return false;

    const currentTime = new Date(currentTimestamp).getTime();
    const previousTime = new Date(previous.createdAt || 0).getTime();
    const timeDiff = currentTime - previousTime;

    // Show separator if more than 2 minutes apart OR if date changed
    const currentDate = new Date(currentTimestamp).toDateString();
    const previousDate = new Date(previous.createdAt || 0).toDateString();
    const dateChanged = currentDate !== previousDate;

    return timeDiff > 120000 || dateChanged;
  };

  const renderTimeSeparator = (dateString?: string) => {
    if (!dateString) return null;

    return (
      <div className="flex items-center justify-center py-2">
        <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs">
          {formatDate(dateString)}
        </div>
      </div>
    );
  };

  const renderMessage = (message: TranscriptItem, index: number) => {
    const isUser = message.role === 'user';
    const isBot = message.role === 'bot';
    const isAgent = message.role === 'agent';

    return (
      <div
        key={index}
        data-message
        data-message-index={index}
        className={cn('flex items-end gap-2', isUser ? 'justify-end' : 'justify-start')}
      >
        <div
          className={cn(
            'max-w-[75%] px-4 py-3 text-base rounded-2xl',
            isUser
              ? 'rounded-br-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-100 rounded-bl-md'
          )}
          style={
            isUser
              ? { ...getBackgroundStyle(theme.accentColor), color: theme.textOverAccentColor }
              : undefined
          }
        >
          {(isBot || isAgent) && (
            <div className="flex items-center gap-2 mb-2">
              {theme.iconUrl ? (
                <img src={theme.iconUrl} alt={agentName} className="h-5 w-5 rounded-full" />
              ) : null}
              <span className="font-medium text-sm text-gray-600 dark:text-gray-400">
                {isAgent
                  ? (message as AgentMessage).agentName || 'Customer Service'
                  : agentName || 'Chat Assistant'}
              </span>
            </div>
          )}
          {'content' in message && !('eventType' in message) && message.content && (
            <div className="break-words">
              {isUser ? message.content : formatMessageContent(removeUserOptions(message.content))}
            </div>
          )}
          {'attachments' in message && renderAttachments(message.attachments, isUser)}
        </div>
      </div>
    );
  };

  const handleAttachmentOpen = (proxiedUrl: string, fileName: string, isImage: boolean) => {
    if (isNativeApp && isImage) {
      setViewerImage(proxiedUrl);
      return;
    }
    // Web: fetch blob and trigger download
    fetch(proxiedUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        window.open(proxiedUrl, '_blank');
      });
  };

  const renderAttachments = (attachments?: MessageAttachment[], isUser = false) => {
    if (!attachments?.length) return null;
    // In native apps, hide agent file attachments (file downloads not supported, images use the viewer)
    const visible = isNativeApp && !isUser ? attachments.filter((a) => a.type === 'image') : attachments;
    if (!visible.length) return null;
    return (
      <div className="mt-2 space-y-2">
        {visible.map((attachment, i) => {
          const proxiedUrl = isUser ? attachment.url : proxyAttachmentUrl(attachment.url, chatId);
          const fallbackName = decodeURIComponent(
            attachment.url.split('?')[0].split('/').pop() || 'download'
          );
          const fileName = attachment.type === 'file' ? attachment.fileName : fallbackName;
          return attachment.type === 'image' ? (
            isNativeApp ? (
              <button
                key={i}
                type="button"
                onClick={() => handleAttachmentOpen(proxiedUrl, fileName, true)}
                className="block cursor-pointer"
              >
                <img src={proxiedUrl} alt={fileName} className="max-h-48 rounded-lg object-contain" />
              </button>
            ) : (
              <div key={i} className="relative group/img inline-block">
                <img src={proxiedUrl} alt={fileName} className="max-h-48 rounded-lg object-contain" />
                <button
                  type="button"
                  onClick={() => handleAttachmentOpen(proxiedUrl, fileName, true)}
                  className="absolute bottom-2 right-2 p-1.5 rounded-md bg-black/60 text-white opacity-0 group-hover/img:opacity-100 transition-opacity hover:bg-black/80 cursor-pointer"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            )
          ) : (
            <button
              key={i}
              type="button"
              onClick={() => handleAttachmentOpen(proxiedUrl, fileName, false)}
              className="group/file flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 dark:bg-black/10 dark:hover:bg-black/20 transition-colors text-sm cursor-pointer text-left max-w-full overflow-hidden"
            >
              <FileText className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{fileName}</span>
              <Download className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover/file:opacity-100 transition-opacity" />
            </button>
          );
        })}
      </div>
    );
  };

  const renderInteractivePrompt = (message: TranscriptItem, index: number) => {
    const toolArgs =
      'toolArguments' in message && typeof message.toolArguments === 'string'
        ? message.toolArguments
        : undefined;
    let prompt = '';
    if (toolArgs) {
      try {
        const parsed = JSON.parse(toolArgs) as { prompt?: string };
        prompt = parsed.prompt || '';
      } catch {
        // ignore parse errors
      }
    }
    if (!prompt) return null;

    return (
      <div key={index} data-message data-message-index={index} className="flex items-end gap-2 justify-start">
        <div className="max-w-[75%] px-4 py-3 text-base rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-100 rounded-bl-md">
          {(theme.iconUrl || agentName) && (
            <div className="flex items-center gap-2 mb-2">
              {theme.iconUrl ? (
                <img src={theme.iconUrl} alt={agentName} className="h-5 w-5 rounded-full" />
              ) : null}
              <span className="font-medium text-sm text-gray-600 dark:text-gray-400">
                {agentName || 'Chat Assistant'}
              </span>
            </div>
          )}
          <div className="break-words">{prompt}</div>
        </div>
      </div>
    );
  };

  const renderTool = (message: TranscriptItem, index: number) => {
    return (
      <div key={index} className="flex items-start gap-2">
        <div className="max-w-[75%] px-4 py-3 text-base rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-100 rounded-bl-md">
          <div className="whitespace-pre-wrap break-words text-xs font-mono">
            {JSON.stringify('content' in message ? message.content : '', null, 2)}
          </div>
        </div>
      </div>
    );
  };

  const renderEvent = (message: TranscriptItem, index: number) => {
    const isJoin = 'eventType' in message && message.eventType === 'conversation:join';
    const isLeave = 'eventType' in message && message.eventType === 'conversation:leave';
    const isEgainConversationEnd = 'eventType' in message && message.eventType === 'eGain:conversationEnd';
    const isZendeskConversationEnd =
      'eventType' in message && message.eventType === 'zendesk:conversationEnd';
    const isNiceCxoneConversationEnd =
      'eventType' in message && message.eventType === 'niceCxone:conversationEnd';
    const isConversationEnd =
      isEgainConversationEnd || isZendeskConversationEnd || isNiceCxoneConversationEnd;

    // For conversation end events, find agent name from previous messages
    if (isConversationEnd) {
      // Get agent name from the event itself, or find from previous agent messages
      const eventAgentName = 'agentName' in message ? message.agentName : undefined;
      const agentNameFromMessages = messages.find(
        (m) => m.role === 'agent' && 'agentName' in m && m.agentName
      ) as AgentMessage | undefined;
      const displayAgentName = eventAgentName || agentNameFromMessages?.agentName;

      // Don't show the tile if no agent name found
      if (!displayAgentName) return null;

      return (
        <div key={index} className="flex items-center justify-center py-2">
          <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs">
            <span>{displayAgentName} ended the conversation</span>
          </div>
        </div>
      );
    }

    if (!isJoin && !isLeave) return null;

    return (
      <div key={index} className="flex items-center justify-center py-2">
        <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs">
          {isJoin && 'eventType' in message && 'userName' in message && (
            <span>{message.userName} joined the conversation</span>
          )}
          {isLeave && 'eventType' in message && 'userName' in message && (
            <span>{message.userName} left the conversation</span>
          )}
        </div>
      </div>
    );
  };

  const filteredMessages = messages.filter((message, i) => {
    if (message.role === 'tool') {
      if (
        'toolName' in message &&
        typeof message.toolName === 'string' &&
        message.toolName in INTERACTIVE_TOOL_NAMES
      ) {
        // Show answered ones (not last message) as the agent's prompt bubble.
        // Show the active (last) one only when dismissed — otherwise it's rendered as a live widget.
        return i < messages.length - 1 || !!componentDismissed;
      }
      // Filter out tool call messages without content
      if ((message as unknown as { toolCalls?: unknown }).toolCalls) {
        return !!(message as unknown as { content?: unknown }).content;
      }
    }
    return true;
  });

  // Track if we've shown the first agent message (exclude events)
  const firstAgentMessageIndex = filteredMessages.findIndex(
    (msg) => msg.role === 'agent' && !('eventType' in msg)
  );

  return (
    <div className={cn('space-y-4', className)}>
      {filteredMessages.map((item, index) => {
        const previousItem = index > 0 ? filteredMessages[index - 1] : undefined;
        const showTimeSeparator = index === 0 || shouldShowTimeSeparator(item, previousItem);
        const isFirstAgentMessage = index === firstAgentMessageIndex;

        const renderItem = () => {
          if ('eventType' in item) {
            return renderEvent(item, index);
          }
          if (item.role === 'tool') {
            if (
              'toolName' in item &&
              typeof item.toolName === 'string' &&
              item.toolName in INTERACTIVE_TOOL_NAMES
            ) {
              return renderInteractivePrompt(item, index);
            }
            return renderTool(item, index);
          }
          return renderMessage(item, index);
        };

        return (
          <div key={index}>
            {showTimeSeparator && renderTimeSeparator(item.createdAt)}
            {isFirstAgentMessage && (
              <div className="flex flex-col items-center justify-center py-3">
                <div className="relative flex items-center justify-center w-10 h-10 mb-2">
                  <div
                    className="absolute inset-0 rounded-full opacity-10"
                    style={getBackgroundStyle(theme.accentColor)}
                  />
                  <Headphones className="h-5 w-5 relative z-10" style={{ color: theme.accentColor }} />
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Agent connected</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  You're now talking to {(item as AgentMessage).agentName || 'Customer Service'}
                </div>
              </div>
            )}
            {renderItem()}
          </div>
        );
      })}
      {endedAt && (
        <>
          <div className="flex items-center justify-center py-2">
            <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs">
              Conversation ended • {formatDate(endedAt)}
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 py-4 px-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-100">
              Overall, how was your chat experience?
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRatingClick(true)}
                disabled={csatIsSatisfied !== undefined}
                className={cn(
                  'flex items-center gap-2',
                  csatIsSatisfied === true
                    ? 'bg-green-50 border-green-500 text-green-700 dark:bg-green-900 dark:border-green-600 dark:text-green-200'
                    : 'hover:bg-green-50 hover:border-green-200 dark:hover:bg-green-900/50 dark:hover:border-green-700',
                  csatIsSatisfied !== undefined && csatIsSatisfied !== true && 'opacity-40'
                )}
              >
                <ThumbsUp className="h-4 w-4" />
                Good
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRatingClick(false)}
                disabled={csatIsSatisfied !== undefined}
                className={cn(
                  'flex items-center gap-2',
                  csatIsSatisfied === false
                    ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900 dark:border-red-600 dark:text-red-200'
                    : 'hover:bg-red-50 hover:border-red-200 dark:hover:bg-red-900/50 dark:hover:border-red-700',
                  csatIsSatisfied !== undefined && csatIsSatisfied !== false && 'opacity-40'
                )}
              >
                <ThumbsDown className="h-4 w-4" />
                Bad
              </Button>
            </div>
            {csatIsSatisfied !== undefined && (
              <p className="text-xs text-gray-600 dark:text-gray-400">Thank you for your feedback!</p>
            )}
          </div>
        </>
      )}
      {viewerImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setViewerImage(null)}
        >
          <button
            type="button"
            onClick={() => setViewerImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={viewerImage}
            alt="Attachment"
            className="max-w-[90%] max-h-[90%] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
